import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Badge, ButtonLink } from "@/components/play";
import { ArticleVideo } from "@/components/ArticleVideo";
import { authLink } from "@/components/onboarding/authContinuation";
import { categoryName, filmArticle, formatArticleDate } from "@/lib/insights";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { SitePage } from "@/components/SitePage";
import insightsData from "../data/insights.json";

/** News (/insights, and /news redirects here): the film, playing on the page, then the workshop's other articles as cards, each one link named by its title. */
export function InsightsPage() {
  useDocumentTitle("News · StreamBridge");
  return (
    <SitePage mainId="insights-main">
      <section className="px-6 pb-10 pt-16">
        <div className="mx-auto max-w-5xl text-center">
          <p className="mb-6 text-size-xs font-medium text-neutral-7">News</p>
          <h1 className="mb-6 text-4xl font-black text-neutral-10 sm:text-5xl">Notes from the workshop</h1>
          <p className="mx-auto max-w-2xl text-lg text-neutral-7">Guides to cameras, live shows and the tech behind them.</p>
        </div>
      </section>

      {filmArticle && (
        <section className="px-6 pb-16" aria-labelledby={`insight-${filmArticle.id}-title`}>
          <div className="mx-auto max-w-4xl">
            <ArticleVideo wide src={filmArticle.video} poster={filmArticle.poster} captions={filmArticle.captions} label={`${filmArticle.title}: the film`} />
            <Badge tone="accent" className="mt-4">Demonstration</Badge>
            <h2 id={`insight-${filmArticle.id}-title`} className="mt-3 text-2xl font-black text-neutral-10">
              <Link to={`/insights/${filmArticle.slug}`} className="underline underline-offset-4 hover:text-neutral-8">{filmArticle.title}</Link>
            </h2>
            <p className="mt-2 text-neutral-7">{filmArticle.description}</p>
          </div>
        </section>
      )}

      <section className="px-6 pb-24" aria-label="Articles">
        <ul className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2" role="list">
          {insightsData.articles.filter((article) => article !== filmArticle).map((article) => (
            <li key={article.id}>
              <Link to={`/insights/${article.slug}`} className="group block h-full rounded-2xl" aria-labelledby={`insight-${article.id}-title`} aria-describedby={`insight-${article.id}-summary`}>
                <article className="h-full overflow-hidden rounded-2xl bg-neutral-2">
                  <div className="relative aspect-video overflow-hidden bg-neutral-9">
                    <img src={article.image} alt="" className="h-full w-full object-contain" loading="lazy" />
                    <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                      {article.categories.map((category) => (
                        <Badge key={category} tone="overlay">{categoryName(category)}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="p-6">
                    <time dateTime={article.date} className="text-sm text-neutral-7">{formatArticleDate(article.date)}</time>
                    <h2 id={`insight-${article.id}-title`} className="mt-2 text-xl font-black text-neutral-10 underline-offset-4 group-hover:underline">{article.title}</h2>
                    <p id={`insight-${article.id}-summary`} className="mt-3 line-clamp-2 text-neutral-7">{article.description}</p>
                  </div>
                </article>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="px-6 pb-24" aria-labelledby="insights-cta-title">
        <div className="mx-auto max-w-4xl rounded-3xl bg-neutral-2 p-12 text-center">
          <h2 id="insights-cta-title" className="mb-4 text-3xl font-black text-neutral-10">Bring your event to life.</h2>
          <p className="mb-8 text-neutral-7">Create an event and bring your cameras.</p>
          <ButtonLink to={authLink("/signup", "/app?create=1")}>
            Create event
            <ArrowRight aria-hidden="true" />
          </ButtonLink>
        </div>
      </section>
    </SitePage>
  );
}

export default InsightsPage;
